import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { FiEdit2, FiTrash2, FiPlus, FiSave } from 'react-icons/fi';

const CategoriesManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCategory, setCurrentCategory] = useState({ name: '', slug: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'blog_categories'));
      const list = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCategories(list);
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (currentCategory.id) {
        await updateDoc(doc(db, 'blog_categories', currentCategory.id), {
          name: currentCategory.name,
          slug: currentCategory.slug || currentCategory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'blog_categories'), {
          name: currentCategory.name,
          slug: currentCategory.slug || currentCategory.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          createdAt: serverTimestamp()
        });
      }
      setIsEditing(false);
      setCurrentCategory({ name: '', slug: '' });
      fetchCategories();
    } catch (error) {
      console.error("Erro ao salvar categoria:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Tem certeza que deseja excluir esta categoria?")) {
      try {
        await deleteDoc(doc(db, 'blog_categories', id));
        fetchCategories();
      } catch (error) {
        console.error("Erro ao deletar:", error);
      }
    }
  };

  const handleEdit = (cat) => {
    setCurrentCategory(cat);
    setIsEditing(true);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Carregando categorias...</div>;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Gerenciar Categorias</h2>
          <p className="text-gray-500">Adicione ou edite categorias do blog.</p>
        </div>
        {!isEditing && (
          <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors">
            <FiPlus /> Nova Categoria
          </button>
        )}
      </div>

      {isEditing && (
        <form onSubmit={handleSave} className="mb-8 bg-gray-50 dark:bg-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome da Categoria</label>
              <input type="text" required value={currentCategory.name} onChange={e => setCurrentCategory({...currentCategory, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug (URL amigável)</label>
              <input type="text" value={currentCategory.slug} onChange={e => setCurrentCategory({...currentCategory, slug: e.target.value})} placeholder="Deixe em branco para gerar auto" className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => { setIsEditing(false); setCurrentCategory({name:'', slug:''}); }} className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancelar</button>
            <button type="submit" className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"><FiSave /> Salvar</button>
          </div>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 text-sm">
              <th className="pb-3 font-semibold">Nome</th>
              <th className="pb-3 font-semibold">Slug</th>
              <th className="pb-3 font-semibold text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(cat => (
              <tr key={cat.id} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <td className="py-4 text-gray-900 dark:text-white font-medium">{cat.name}</td>
                <td className="py-4 text-gray-500 dark:text-gray-400 text-sm">{cat.slug}</td>
                <td className="py-4 flex justify-end gap-2">
                  <button onClick={() => handleEdit(cat)} className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 rounded-lg"><FiEdit2 /></button>
                  <button onClick={() => handleDelete(cat.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-gray-700 rounded-lg"><FiTrash2 /></button>
                </td>
              </tr>
            ))}
            {categories.length === 0 && (
              <tr><td colSpan="3" className="py-8 text-center text-gray-500">Nenhuma categoria encontrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CategoriesManager;
